import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE from "./config";

const MailCenter = () => {
    const token = localStorage.getItem("access_token");

    const auth = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState("");

    const [options, setOptions] = useState({
        segments: [],
        registrations: [],
        team_members: [],
    });

    const [targetType, setTargetType] = useState("all_event");
    const [segmentId, setSegmentId] = useState("");
    const [registrationId, setRegistrationId] = useState("");
    const [teamMemberId, setTeamMemberId] = useState("");
    const [includeTeamMembers, setIncludeTeamMembers] = useState(false);
    const [customEmails, setCustomEmails] = useState("");

    const [subject, setSubject] = useState("");
    const [messageHtml, setMessageHtml] = useState("");
    const [ctaText, setCtaText] = useState("");
    const [ctaUrl, setCtaUrl] = useState("");

    const [preview, setPreview] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState(null);

    const loadCampaigns = async () => {
        if (!eventId) return;

        try {
            const res = await axios.get(
                `${API_BASE}/api/portal/mail-campaigns/?event_id=${eventId}`,
                auth
            );

            setCampaigns(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Campaign load failed:", err);
        }
    };

    useEffect(() => {
        if (!token) return;

        axios
            .get(`${API_BASE}/api/portal/events/`, auth)
            .then((res) => {
                const eventList = Array.isArray(res.data) ? res.data : [];
                setEvents(eventList);

                if (eventList.length > 0) {
                    setEventId(String(eventList[0].id));
                }
            })
            .catch((err) => {
                console.error("Event load failed:", err);
            });
    }, []);

    useEffect(() => {
        if (!eventId || !token) return;

        axios
            .get(
                `${API_BASE}/api/portal/mail-campaigns/recipient-options/?event_id=${eventId}`,
                auth
            )
            .then((res) => {
                setOptions({
                    segments: Array.isArray(res.data?.segments) ? res.data.segments : [],
                    registrations: Array.isArray(res.data?.registrations) ? res.data.registrations : [],
                    team_members: Array.isArray(res.data?.team_members) ? res.data.team_members : [],
                });
            })
            .catch((err) => {
                console.error("Recipient options failed:", err);
                setOptions({
                    segments: [],
                    registrations: [],
                    team_members: [],
                });
            });

        loadCampaigns();
    }, [eventId]);

    const buildPayload = () => {
        const parsedCustomEmails = customEmails
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean);

        return {
            event: Number(eventId),
            target_type: targetType,
            segment: segmentId ? Number(segmentId) : null,
            registration: registrationId ? Number(registrationId) : null,
            team_member: teamMemberId ? Number(teamMemberId) : null,
            include_team_members: includeTeamMembers,
            custom_emails: parsedCustomEmails,
            subject,
            message_html: messageHtml,
            cta_text: ctaText,
            cta_url: ctaUrl,
        };
    };

    const handlePreview = async () => {
        if (!eventId) {
            alert("Select an event first.");
            return;
        }

        setLoading(true);

        try {
            const res = await axios.post(
                `${API_BASE}/api/portal/mail-campaigns/preview/`,
                buildPayload(),
                auth
            );

            setPreview(res.data);
        } catch (err) {
            console.error("Preview failed:", err.response?.data || err);
            alert(err.response?.data?.error || "Preview failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleTest = async () => {
        if (!subject.trim() || !messageHtml.trim()) {
            alert("Subject and message are required.");
            return;
        }

        setLoading(true);

        try {
            await axios.post(
                `${API_BASE}/api/portal/mail-campaigns/send-test/`,
                {
                    event: Number(eventId),
                    subject,
                    message_html: messageHtml,
                },
                auth
            );

            alert("Test email sent.");
        } catch (err) {
            console.error("Test email failed:", err.response?.data || err);
            alert(err.response?.data?.error || "Test email failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!eventId || !subject.trim() || !messageHtml.trim()) {
            alert("Event, subject and message are required.");
            return;
        }

        const confirmSend = window.confirm(
            `Start this mail campaign?\n${preview?.count
                ? `Recipients: ${preview.count}`
                : "Preview recommended before sending."
            }`
        );

        if (!confirmSend) return;

        setSending(true);

        setProgress({
            status: "creating",
            sent: 0,
            failed: 0,
            remaining: null,
        });

        try {
            const createRes = await axios.post(
                `${API_BASE}/api/portal/mail-campaigns/`,
                buildPayload(),
                auth
            );

            const campaignId = createRes.data.id;

            if (!campaignId) {
                throw new Error("Campaign ID not returned from backend.");
            }

            let completed = false;

            while (!completed) {
                const batchRes = await axios.post(
                    `${API_BASE}/api/portal/mail-campaigns/${campaignId}/send-batch/`,
                    {},
                    auth
                );

                const data = batchRes.data;

                setProgress({
                    status: data.status,
                    sent: data.sent || 0,
                    failed: data.failed || 0,
                    remaining: data.remaining ?? 0,
                    sent_now: data.sent_now || 0,
                    failed_now: data.failed_now || 0,
                });

                if (data.status === "completed") {
                    completed = true;

                    alert(
                        `Campaign completed.\nSent: ${data.sent}\nFailed: ${data.failed}`
                    );

                    break;
                }

                await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            setSubject("");
            setMessageHtml("");
            setPreview(null);
            setCustomEmails("");
            setRegistrationId("");
            setTeamMemberId("");
            setSegmentId("");

            await loadCampaigns();
        } catch (err) {
            console.error("Campaign send failed:", err.response?.data || err);

            alert(
                err.response?.data?.error ||
                err.message ||
                "Campaign failed."
            );
        } finally {
            setSending(false);
        }
    };

    const resumeCampaign = async (campaignId) => {
        const yes = window.confirm("Resume this campaign from pending recipients only?");
        if (!yes) return;

        setSending(true);

        try {
            let completed = false;

            while (!completed) {
                const batchRes = await axios.post(
                    `${API_BASE}/api/portal/mail-campaigns/${campaignId}/send-batch/`,
                    {},
                    auth
                );

                const data = batchRes.data;

                setProgress({
                    status: data.status,
                    sent: data.sent || 0,
                    failed: data.failed || 0,
                    remaining: data.remaining ?? 0,
                    sent_now: data.sent_now || 0,
                    failed_now: data.failed_now || 0,
                });

                if (data.status === "completed") {
                    completed = true;
                    alert(`Campaign completed.\nSent: ${data.sent}\nFailed: ${data.failed}`);
                    break;
                }

                await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            await loadCampaigns();
        } catch (err) {
            console.error("Resume failed:", err.response?.data || err);
            alert(err.response?.data?.error || "Resume failed.");
        } finally {
            setSending(false);
        }
    };

    if (!token) {
        return (
            <div className="text-white p-10">
                Authentication expired. Please login again.
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto animate-in fade-in duration-500 text-white">
            <header className="mb-10">
                <p className="text-sky-500 text-[10px] font-bold uppercase tracking-[0.4em] mb-2">
                    Communication Center
                </p>

                <h2 className="text-3xl font-black uppercase tracking-tighter">
                    Mail Center
                </h2>

                <p className="text-gray-500 text-sm mt-2">
                    Send mail to all participants, segment-wise participants, verified users, team members, or individual participants.
                </p>
            </header>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 bg-white/[0.03] border border-white/10 rounded-[2rem] p-6 space-y-5">

                    <div>
                        <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                            Event
                        </label>

                        <select
                            value={eventId}
                            onChange={(e) => {
                                setEventId(e.target.value);
                                setPreview(null);
                                setSegmentId("");
                                setRegistrationId("");
                                setTeamMemberId("");
                            }}
                            className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                        >
                            {events.map((event) => (
                                <option
                                    key={event.id}
                                    value={event.id}
                                    className="bg-[#111]"
                                >
                                    {event.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                            Target Audience
                        </label>

                        <select
                            value={targetType}
                            onChange={(e) => {
                                setTargetType(e.target.value);
                                setPreview(null);
                                setSegmentId("");
                                setRegistrationId("");
                                setTeamMemberId("");
                            }}
                            className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                        >
                            <option value="all_event">All Event Participants</option>
                            <option value="segment">Specific Segment</option>
                            <option value="verified">Verified Participants</option>
                            <option value="pending">Pending Participants</option>
                            <option value="checked_in">Checked-in Participants</option>
                            <option value="segment_verified">Specific Segment + Verified</option>
                            <option value="segment_pending">Specific Segment + Pending</option>
                            <option value="segment_checked_in">Specific Segment + Checked-in</option>
                            <option value="individual_registration">Individual Participant</option>
                            <option value="individual_team_member">Individual Team Member</option>
                            <option value="custom_emails">Custom Email List</option>
                        </select>
                    </div>

                    {targetType.includes("segment") && (
                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Select Segment
                            </label>

                            <select
                                value={segmentId}
                                onChange={(e) => {
                                    setSegmentId(e.target.value);
                                    setPreview(null);
                                }}
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                            >
                                <option value="">Choose segment</option>

                                {options.segments.map((segment) => (
                                    <option
                                        key={segment.id}
                                        value={segment.id}
                                        className="bg-[#111]"
                                    >
                                        {segment.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {targetType === "individual_registration" && (
                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Select Participant
                            </label>

                            <select
                                value={registrationId}
                                onChange={(e) => {
                                    setRegistrationId(e.target.value);
                                    setPreview(null);
                                }}
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                            >
                                <option value="">Choose participant</option>

                                {options.registrations.map((reg) => (
                                    <option
                                        key={reg.id}
                                        value={reg.id}
                                        className="bg-[#111]"
                                    >
                                        {reg.lead_name} — {reg.email} — {reg.segment__title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {targetType === "individual_team_member" && (
                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Select Team Member
                            </label>

                            <select
                                value={teamMemberId}
                                onChange={(e) => {
                                    setTeamMemberId(e.target.value);
                                    setPreview(null);
                                }}
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                            >
                                <option value="">Choose team member</option>

                                {options.team_members.map((member) => (
                                    <option
                                        key={member.id}
                                        value={member.id}
                                        className="bg-[#111]"
                                    >
                                        {member.name} — {member.email} — {member.team_group__team_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {targetType === "custom_emails" && (
                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Custom Emails
                            </label>

                            <textarea
                                value={customEmails}
                                onChange={(e) => {
                                    setCustomEmails(e.target.value);
                                    setPreview(null);
                                }}
                                placeholder="email1@gmail.com, email2@gmail.com"
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white min-h-[90px]"
                            />
                        </div>
                    )}

                    <label className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-2xl p-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeTeamMembers}
                            onChange={(e) => {
                                setIncludeTeamMembers(e.target.checked);
                                setPreview(null);
                            }}
                            className="w-4 h-4 accent-sky-500"
                        />

                        <div>
                            <p className="text-sm font-bold text-white">
                                Include team members also
                            </p>

                            <p className="text-[10px] text-gray-500 mt-0.5">
                                Useful for team-based segments where every member should receive the announcement.
                            </p>
                        </div>
                    </label>

                    <div>
                        <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                            Subject
                        </label>

                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Important update about your event"
                            className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                            Message
                        </label>

                        <textarea
                            value={messageHtml}
                            onChange={(e) => setMessageHtml(e.target.value)}
                            placeholder="Write your message here..."
                            className="w-full bg-black border border-white/10 rounded-xl p-3 text-white min-h-[180px]"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Button Text
                            </label>

                            <input
                                value={ctaText}
                                onChange={(e) => setCtaText(e.target.value)}
                                placeholder="Open Rulebook"
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
                                Button URL
                            </label>

                            <input
                                value={ctaUrl}
                                onChange={(e) => setCtaUrl(e.target.value)}
                                placeholder="https://..."
                                className="w-full bg-black border border-white/10 rounded-xl p-3 text-white"
                            />
                        </div>

                    </div>

                    <div className="flex gap-3 flex-wrap">
                        <button
                            onClick={handlePreview}
                            disabled={loading || sending}
                            className="bg-white text-black px-5 py-3 rounded-xl font-bold disabled:opacity-40"
                        >
                            Preview Count
                        </button>

                        <button
                            onClick={handleTest}
                            disabled={loading || sending}
                            className="bg-white/10 border border-white/10 px-5 py-3 rounded-xl font-bold disabled:opacity-40"
                        >
                            Send Test
                        </button>

                        <button
                            onClick={handleSend}
                            disabled={loading || sending}
                            className="bg-sky-500 text-black px-5 py-3 rounded-xl font-bold disabled:opacity-40"
                        >
                            {sending ? "Sending..." : "Start Campaign"}
                        </button>
                    </div>

                    {preview && (
                        <div className="bg-sky-500/10 border border-sky-500/20 rounded-2xl p-4">
                            <p className="text-sm font-black text-sky-300">
                                Recipient Count: {preview.count}
                            </p>

                            {preview.sample?.length > 0 && (
                                <p className="text-xs text-gray-400 mt-2 break-words">
                                    Sample: {preview.sample.map((s) => s.email).join(", ")}
                                </p>
                            )}
                        </div>
                    )}

                    {progress && (
                        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4">
                            <p className="text-sm font-black text-emerald-300 uppercase">
                                Status: {progress.status}
                            </p>

                            <p className="text-xs text-gray-400 mt-1">
                                Sent: {progress.sent} · Failed: {progress.failed} · Remaining: {progress.remaining ?? "—"}
                            </p>
                        </div>
                    )}
                </div>

                <div className="bg-white/[0.03] border border-white/10 rounded-[2rem] p-6">
                    <h3 className="text-xl font-black text-white uppercase tracking-tight mb-5">
                        Campaign History
                    </h3>

                    <div className="space-y-3 max-h-[650px] overflow-y-auto pr-1">
                        {campaigns.map((campaign) => (
                            <div
                                key={campaign.id}
                                className="bg-black/40 border border-white/10 rounded-2xl p-4"
                            >
                                <p className="font-black text-white text-sm leading-tight">
                                    {campaign.subject}
                                </p>

                                <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-widest">
                                    {campaign.status} · Sent {campaign.sent_count}/{campaign.total_count} · Failed {campaign.failed_count}
                                </p>

                                {campaign.segment_title && (
                                    <p className="text-[10px] text-sky-400 mt-1">
                                        Segment: {campaign.segment_title}
                                    </p>
                                )}
                                {campaign.status !== "completed" && campaign.status !== "cancelled" && (
                                    <button
                                        onClick={() => resumeCampaign(campaign.id)}
                                        disabled={sending}
                                        className="mt-3 bg-sky-500 text-black px-4 py-2 rounded-xl text-[10px] font-black uppercase disabled:opacity-40"
                                    >
                                        Resume Campaign
                                    </button>
                                )}
                            </div>
                        ))}

                        {campaigns.length === 0 && (
                            <p className="text-gray-600 text-sm">
                                No campaigns yet.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MailCenter;